import React, { useState } from 'react';
import { Box, IconButton, Typography, Tooltip } from '@mui/material';
import { styled } from '@mui/material/styles';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../store/store';
import { hideAlert } from '../../store/features/auth/alertSlice';

const AlertContainer = styled(Box)<{ status: string }>(({ theme, status }) => ({
  backgroundColor:
    status === 'success' ? theme.palette.success.main :
    status === 'error' ? theme.palette.error.main :
    status === 'warn' || status === 'warning' ? theme.palette.warning.main :
    status === 'info' ? theme.palette.info.main :
    theme.palette.primary.dark,
  borderRadius: '8px',
  padding: '10px 20px',
  color: theme.palette.getContrastText(
    status === 'success' ? theme.palette.success.main :
    status === 'error' ? theme.palette.error.main :
    status === 'warn' || status === 'warning' ? theme.palette.warning.main :
    status === 'info' ? theme.palette.info.main :
    theme.palette.primary.dark
  ),
  margin: '10px 30px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 10,
}));

const AlertBox: React.FC = () => {
  const dispatch = useDispatch();
  const { alerts } = useSelector((state: RootState) => state.alert);
  const [copiedMessage, setCopiedMessage] = useState<string | null>(null);

  const handleCopy = (text: string) => {
    navigator.clipboard.writeText(text);
    setCopiedMessage(text);
    setTimeout(() => setCopiedMessage(null), 1500);
  };

  if (alerts.length === 0) return null;

  return (
    <>
      {alerts.map(({ id, status, message }) => {
        const StatusIcon = status === 'error' ? WarningAmberIcon : CheckCircleOutlineIcon;

        return (
          <AlertContainer key={id} status={status}>
            <Box display="flex" flexDirection="column">
              {Object.entries(message).map(([title, msg], index) => (
                <Box key={index} display="flex" alignItems="center">
                  <StatusIcon sx={{ fontSize: 28, mr: 1, color: 'white' }} />
                  <Typography variant="h6" sx={{ color: 'white', mr: 1 }}>
                    {title}: {msg}
                  </Typography>
                  <Tooltip title={copiedMessage === `${title}: ${msg}` ? 'Copied!' : 'Copy'} arrow>
                    <IconButton
                      aria-label="Copy message"
                      onClick={() => handleCopy(`${title}: ${msg}`)}
                      sx={{ color: 'white', p: 0.5 }}
                    >
                      {copiedMessage === `${title}: ${msg}` ? (
                        <CheckIcon sx={{ fontSize: 20, color: 'lightgreen' }} />
                      ) : (
                        <ContentCopyIcon sx={{ fontSize: 20 }} />
                      )}
                    </IconButton>
                  </Tooltip>
                </Box>
              ))}
            </Box>

            <IconButton
              aria-label="Close alert"
              onClick={() => dispatch(hideAlert(id))}
              sx={{ color: 'white', ml: 'auto' }}
            >
              <CloseIcon sx={{ fontSize: 22 }} />
            </IconButton>
          </AlertContainer>
        );
      })}
    </>
  );
};

export default AlertBox;
